import { ArrowUpRight, Broadcast, WarningCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { validLiveUrl, type PublicExaminer } from "../lib/publicExaminers";

type ExaminerLiveBoardProps = {
  online: PublicExaminer[];
  offline: PublicExaminer[];
  loading: boolean;
  error: string | null;
};

function ExaminerAvatar({ examiner, compact = false }: { examiner: PublicExaminer; compact?: boolean }) {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <div className={`live-examiner-avatar${compact ? " is-compact" : ""}`}>
      {examiner.avatarUrl && !imageFailed
        ? <img src={examiner.avatarUrl} alt={`${examiner.displayName}头像`} onError={() => setImageFailed(true)} />
        : <span>{examiner.avatarSeed}</span>}
    </div>
  );
}

function LiveExaminerCard({ examiner, index }: { examiner: PublicExaminer; index: number }) {
  const liveUrl = validLiveUrl(examiner);
  return (
    <article className="live-examiner-card">
      <header>
        <span><i />LIVE NOW</span>
        <small>{String(index + 1).padStart(2, "0")}</small>
      </header>
      <div className="live-examiner-card__profile">
        <ExaminerAvatar examiner={examiner} />
        <div>
          <h3>{examiner.displayName}</h3>
          <span>{examiner.livePlatform || "野羊电竞"} · 考核官</span>
          {examiner.bio && <p>{examiner.bio}</p>}
        </div>
      </div>
      <footer>
        <span><Broadcast size={16} weight="fill" />正在直播</span>
        {liveUrl ? (
          <a href={liveUrl} target="_blank" rel="noreferrer">
            进入直播间<ArrowUpRight size={18} weight="bold" />
          </a>
        ) : (
          <span className="live-link-unavailable"><WarningCircle size={17} />直播链接暂不可用</span>
        )}
      </footer>
    </article>
  );
}

export function ExaminerLiveBoard({ online, offline, loading, error }: ExaminerLiveBoardProps) {
  const hasExaminers = online.length + offline.length > 0;

  if (loading && !hasExaminers) {
    return (
      <div className="live-board-loading" role="status">
        <i />正在同步考官直播状态…
      </div>
    );
  }

  if (error && !hasExaminers) {
    return <div className="live-board-error"><WarningCircle size={20} />{error}</div>;
  }

  return (
    <div className="examiner-live-board">
      <section className="live-board-zone" aria-labelledby="online-examiners-title">
        <header className="live-board-zone__heading">
          <div>
            <span>LIVE SIGNAL</span>
            <h3 id="online-examiners-title">正在直播</h3>
          </div>
          <strong>{String(online.length).padStart(2, "0")}</strong>
        </header>
        {online.length ? (
          <div className="live-examiner-grid">
            {online.map((examiner, index) => <LiveExaminerCard examiner={examiner} index={index} key={examiner.id} />)}
          </div>
        ) : (
          <div className="no-live-state">
            <Broadcast size={26} />
            <div><strong>当前暂无考官直播</strong><span>直播状态每 30 秒更新，可以稍后再来看。</span></div>
          </div>
        )}
      </section>

      {offline.length > 0 && (
        <section className="live-board-zone live-board-zone--roster" aria-labelledby="offline-examiners-title">
          <header className="live-board-zone__heading">
            <div>
              <span>EXAMINER ROSTER</span>
              <h3 id="offline-examiners-title">其他考官</h3>
            </div>
            <strong>{String(offline.length).padStart(2, "0")}</strong>
          </header>
          <div className="examiner-roster-grid">
            {offline.map((examiner) => (
              <article className="examiner-roster-card" key={examiner.id}>
                <ExaminerAvatar examiner={examiner} compact />
                <div><h4>{examiner.displayName}</h4><span>{examiner.livePlatform || "野羊电竞"} · 考核官</span></div>
                <small><i />离线</small>
              </article>
            ))}
          </div>
        </section>
      )}

      {!hasExaminers && !error && <div className="live-board-error">考官资料正在接入，请稍后刷新。</div>}
      {error && hasExaminers && <div className="live-board-stale"><WarningCircle size={16} />{error} 当前保留上次结果。</div>}
    </div>
  );
}
